import * as React from "react"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { DayPicker } from "react-day-picker"
import { cn } from "../../lib/utils"
import { buttonVariants } from "./button"

function Calendar({
  className,
  showOutsideDays,
  mode,
  selected,
  onSelect,
  classNames,
  reservedDates = [], // Add this prop
  ...props
}) {
  // Add function to check if a date is reserved
  const isReserved = React.useCallback((date) => {
  return reservedDates.some(reservation => {
    const start = new Date(reservation.start_time);
    const end = new Date(reservation.end_time);
    start.setHours(0, 0, 0, 0);
    end.setHours(23, 59, 59, 999);
    const checkDate = new Date(date);
    return checkDate >= start && checkDate <= end;
  });
  }, [reservedDates]);

  // Add function to check if a date is in the selected range
  const isInRange = React.useCallback((date) => {
  if (!selected || selected.length !== 2) return false;
  const [start, end] = selected;
  return date > start && date < end;
  }, [selected]);

  // Add function to check if a date is the start of the selected range
  const isStart = React.useCallback((date) => {
  if (!selected || selected.length !== 2) return false;
  const [start] = selected;
  return date.getTime() === start.getTime();
  }, [selected]);

  // Add function to check if a date is the end of the selected range
  const isEnd = React.useCallback((date) => {
  if (!selected || selected.length !== 2) return false;
  const [, end] = selected;
  return date.getTime() === end.getTime();
  }, [selected]);

  // Add modifiers for reserved dates and range dates
  const modifiers = {
  reserved: isReserved,
  range: isInRange,
  start: isStart,
  end: isEnd,
  };

  const modifiersStyles = {
  reserved: {
    backgroundColor: 'rgb(243 232 255)', // Tailwind's purple-100
    color: 'rgb(126 34 206)', // Tailwind's purple-700
    border: '1px solid rgb(233 213 255)', // Tailwind's purple-200
    cursor: 'not-allowed',
  },
  range: {
    backgroundColor: 'rgba(126, 34, 206, 0.2)', // Lighter version of purple
  },
  start: {
    backgroundColor: 'rgb(126 34 206)', // Tailwind's purple-700
    color: 'white',
    borderTopLeftRadius: '50%',
    borderBottomLeftRadius: '50%',
  },
  end: {
    backgroundColor: 'rgb(126 34 206)', // Tailwind's purple-700
    color: 'white',
    borderTopRightRadius: '50%',
    borderBottomRightRadius: '50%',
  },
  };

  const darkModeClass = "dark:[&.rdp-day_button.reserved]:bg-purple-900/30 dark:[&.rdp-day_button.reserved]:text-purple-400 dark:[&.rdp-day_button.reserved]:border-purple-800";


  return (
  <DayPicker
    showOutsideDays={true}
    mode={mode}
    selected={selected}
    onSelect={onSelect}
    modifiers={modifiers}
    modifiersStyles={modifiersStyles}
    
    classNames={{
    caption: "col-span-7 text-center",
    weekdays: "grid grid-cols-7 gap-1 items-center",
    body: "grid grid-cols-7 gap-1 items-center",
    week: "grid grid-cols-7 gap-1 items-center text-center",  
    day: cn(
      "flex items-center justify-center h-8 rounded-full w-full transition-colors",
      buttonVariants.default,
      classNames?.day
    ),
    today: "bg-accent text-muted-foreground",
    selected: "bg-primary text-primary-foreground hover:bg-primary",
    disabled: "text-neutral-400 cursor-not-allowed",
    outside: "text-neutral-400",
    nav: "flex items-center justify-end col-span-1",
    nav_button_previous: "mr-2",
    nav_button_next: "",
    }}
    {...props}
  />
  )
}

export { Calendar }